import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SettingsIcon, UserIcon, BookmarkIcon, BellIcon, LogOutIcon, ExternalLinkIcon, TicketIcon, HeartIcon, CalendarIcon, CheckCircleIcon, GlobeIcon, KeyIcon, ShieldIcon, CreditCardIcon, EyeIcon, BellOffIcon, MailIcon } from 'lucide-react';
const UserProfile = () => {
  const [activeSection, setActiveSection] = useState('profile');
  const [activeSettingsTab, setActiveSettingsTab] = useState('account');
  const [activeNotificationsTab, setActiveNotificationsTab] = useState('email');
  // Mock user data
  const user = {
    name: 'John Kamau',
    email: 'john.kamau@example.com',
    joinedCommunities: 5,
    interests: ['Running', 'Technology', 'Photography'],
    socialLinks: [{
      name: 'Twitter',
      url: 'https://twitter.com/johnkamau'
    }, {
      name: 'LinkedIn',
      url: 'https://linkedin.com/in/johnkamau'
    }, {
      name: 'Instagram',
      url: 'https://instagram.com/johnkamau'
    }]
  };
  const myCommunities = [{
    id: '1',
    name: 'WeRun Nairobi',
    memberCount: 358,
    role: 'Admin',
    icon: '🏃',
    url: 'https://werunnairobi.co.ke'
  }, {
    id: '2',
    name: 'Nairobi Book Club',
    memberCount: 124,
    role: 'Member',
    icon: '📚',
    url: 'https://nairobibookclub.org'
  }, {
    id: '3',
    name: 'Kenya Tech Hub',
    memberCount: 532,
    role: 'Member',
    icon: '💻',
    url: 'https://kenyatechhub.org'
  }];
  const savedItems = [{
    id: '1',
    type: 'community',
    name: 'Mombasa Beach Cleanup',
    description: 'Volunteers dedicated to keeping our beaches clean',
    icon: '🌊',
    url: '/community/mombasa-beach'
  }, {
    id: '2',
    type: 'event',
    name: 'Venture Wars',
    description: 'Startup pitch competition - Aug 20',
    icon: '💼',
    url: '/event/venture-wars'
  }, {
    id: '3',
    type: 'event',
    name: 'Art In Motion Live',
    description: 'Live performance showcase - Aug 22',
    icon: '🎭',
    url: '/event/art-motion'
  }];
  const myTickets = [{
    id: '1',
    eventName: 'Yoga In The Park',
    date: 'Aug 10, 2023',
    time: '7:00 AM',
    location: 'Karura Forest',
    ticketType: 'Standard',
    ticketId: 'YIP-22374',
    qrCode: true,
    url: 'https://kenyabuzz.com/tickets/yip-22374'
  }, {
    id: '2',
    eventName: 'Nairobi Tech Conference',
    date: 'Sep 15, 2023',
    time: '9:00 AM',
    location: 'Radisson Blu Hotel',
    ticketType: 'VIP',
    ticketId: 'NTC-8834',
    qrCode: true,
    url: 'https://eventbrite.com/tickets/ntc-8834'
  }];
  // Account settings options
  const accountSettings = [{
    id: 'personal',
    title: 'Personal Information',
    description: 'Update your personal details and profile',
    icon: <UserIcon className="w-5 h-5" data-id="element-4051" />,
    fields: [{
      name: 'Full Name',
      value: 'John Kamau',
      type: 'text'
    }, {
      name: 'Email',
      value: 'john.kamau@example.com',
      type: 'email'
    }, {
      name: 'Phone',
      value: '+254 700 123 456',
      type: 'tel'
    }, {
      name: 'Location',
      value: 'Nairobi, Kenya',
      type: 'text'
    }, {
      name: 'Bio',
      value: 'Tech enthusiast and community builder',
      type: 'textarea'
    }]
  }, {
    id: 'password',
    title: 'Password & Security',
    description: 'Manage your password and security settings',
    icon: <KeyIcon className="w-5 h-5" data-id="element-4052" />,
    fields: [{
      name: 'Current Password',
      value: '',
      type: 'password'
    }, {
      name: 'New Password',
      value: '',
      type: 'password'
    }, {
      name: 'Confirm New Password',
      value: '',
      type: 'password'
    }]
  }, {
    id: 'privacy',
    title: 'Privacy Settings',
    description: 'Control your privacy and data settings',
    icon: <ShieldIcon className="w-5 h-5" data-id="element-4053" />,
    options: [{
      name: 'Profile visibility',
      value: 'Public',
      choices: ['Public', 'Private', 'Communities Only']
    }, {
      name: 'Show my interests',
      value: true
    }, {
      name: 'Allow others to tag me',
      value: true
    }, {
      name: 'Share my activities',
      value: false
    }]
  }, {
    id: 'payment',
    title: 'Payment Methods',
    description: 'Manage your payment options',
    icon: <CreditCardIcon className="w-5 h-5" data-id="element-4054" />,
    paymentMethods: [{
      type: 'M-Pesa',
      number: '******1234',
      default: true
    }, {
      type: 'Credit Card',
      number: '****-****-****-5678',
      default: false
    }]
  }];
  // Notification settings
  const notificationSettings = [{
    id: 'email',
    title: 'Email Notifications',
    icon: <MailIcon className="w-5 h-5" data-id="element-4055" />,
    options: [{
      name: 'Event reminders',
      value: true
    }, {
      name: 'Community updates',
      value: true
    }, {
      name: 'New messages',
      value: true
    }, {
      name: 'Marketing emails',
      value: false
    }, {
      name: 'Newsletter',
      value: true
    }]
  }, {
    id: 'push',
    title: 'Push Notifications',
    icon: <BellIcon className="w-5 h-5" data-id="element-4056" />,
    options: [{
      name: 'Event reminders',
      value: true
    }, {
      name: 'Community updates',
      value: true
    }, {
      name: 'New messages',
      value: true
    }, {
      name: 'Promotions',
      value: false
    }]
  }, {
    id: 'devices',
    title: 'Device Settings',
    icon: <div className="w-5 h-5" data-id="element-4057" />,
    devices: [{
      name: 'iPhone 13',
      lastActive: 'Today',
      status: 'active'
    }, {
      name: 'MacBook Pro',
      lastActive: 'Yesterday',
      status: 'active'
    }, {
      name: 'iPad Air',
      lastActive: '3 weeks ago',
      status: 'inactive'
    }]
  }, {
    id: 'mute',
    title: 'Muted Content',
    icon: <BellOffIcon className="w-5 h-5" data-id="element-4058" />,
    mutedItems: [{
      type: 'Community',
      name: 'Political Discussions'
    }, {
      type: 'User',
      name: 'Jane Smith'
    }, {
      type: 'Topic',
      name: 'Cryptocurrency'
    }]
  }];
  return <div className="p-4 pb-20" data-id="element-4059">
      <h1 className="text-2xl font-bold mb-4" data-id="element-4060">Profile</h1>
      {/* Profile Header */}
      <div className="flex items-center mb-6" data-id="element-4061">
        <div className="w-20 h-20 bg-gray-300 rounded-full flex items-center justify-center border border-dashed border-gray-400 mr-4" data-id="element-4062">
          <UserIcon className="w-8 h-8 text-gray-500" data-id="element-4063" />
        </div>
        <div data-id="element-4064">
          <h2 className="text-xl font-bold" data-id="element-4065">{user.name}</h2>
          <p className="text-sm text-gray-600" data-id="element-4066">{user.email}</p>
          <p className="text-sm text-gray-600 mt-1" data-id="element-4067">
            Member of {user.joinedCommunities} communities
          </p>
          <div className="flex mt-2 space-x-2" data-id="element-4068">
            {user.socialLinks.map((link, index) => <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs border border-gray-300 rounded-full px-3 py-1 flex items-center hover:bg-gray-50" data-id="element-4069">
                {link.name}
                <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-4070" />
              </a>)}
          </div>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto" data-id="element-4071">
        <button onClick={() => setActiveSection('profile')} className={`py-2 px-4 whitespace-nowrap ${activeSection === 'profile' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4072">
          <UserIcon className="w-4 h-4 inline mr-1" data-id="element-4073" />
          My Profile
        </button>
        <button onClick={() => setActiveSection('saved')} className={`py-2 px-4 whitespace-nowrap ${activeSection === 'saved' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4074">
          <BookmarkIcon className="w-4 h-4 inline mr-1" data-id="element-4075" />
          Saved Items
        </button>
        <button onClick={() => setActiveSection('tickets')} className={`py-2 px-4 whitespace-nowrap ${activeSection === 'tickets' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4076">
          <TicketIcon className="w-4 h-4 inline mr-1" data-id="element-4077" />
          My Tickets
        </button>
        <button onClick={() => setActiveSection('settings')} className={`py-2 px-4 whitespace-nowrap ${activeSection === 'settings' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4078">
          <SettingsIcon className="w-4 h-4 inline mr-1" data-id="element-4079" />
          Settings
        </button>
      </div>
      {/* Profile Section */}
      {activeSection === 'profile' && <>
          <div className="mb-6" data-id="element-4080">
            <h3 className="text-lg font-medium mb-2" data-id="element-4081">My Interests</h3>
            <div className="flex flex-wrap gap-2" data-id="element-4082">
              {user.interests.map((interest, index) => <span key={index} className="border border-gray-300 rounded-full px-3 py-1 text-sm" data-id="element-4083">
                  {interest}
                </span>)}
              <button className="border border-dashed border-gray-400 rounded-full px-3 py-1 text-sm" data-id="element-4084">
                + Add
              </button>
            </div>
          </div>
          <div className="mb-6" data-id="element-4085">
            <h3 className="text-lg font-medium mb-2" data-id="element-4086">My Communities</h3>
            {myCommunities.map(community => <div key={community.id} className="border border-gray-300 rounded-lg p-4 mb-3" data-id="element-4087">
                <div className="flex justify-between items-center" data-id="element-4088">
                  <div className="flex items-center" data-id="element-4089">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center border border-dashed border-gray-400 mr-3" data-id="element-4090">
                      <span className="text-gray-500" data-id="element-4091">{community.icon}</span>
                    </div>
                    <div data-id="element-4092">
                      <h4 className="font-medium" data-id="element-4093">{community.name}</h4>
                      <p className="text-xs text-gray-500" data-id="element-4094">
                        {community.memberCount} members
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-4095">
                    <span className="text-xs bg-gray-200 rounded-full px-2 py-1" data-id="element-4096">
                      {community.role}
                    </span>
                    <a href={community.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-gray-100 rounded" data-id="element-4097">
                      <ExternalLinkIcon className="w-4 h-4 text-gray-500" data-id="element-4098" />
                    </a>
                  </div>
                </div>
              </div>)}
            <button className="w-full border border-dashed border-gray-400 rounded-lg p-3 text-sm text-gray-500 hover:bg-gray-50" data-id="element-4099">
              + Discover More Communities
            </button>
          </div>
          <div data-id="element-4100">
            <h3 className="text-lg font-medium mb-2" data-id="element-4101">Upcoming Events</h3>
            <div className="border border-gray-300 rounded-lg p-4" data-id="element-4102">
              <div className="flex items-center justify-center text-gray-500 py-8" data-id="element-4103">
                <div className="text-center" data-id="element-4104">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2" data-id="element-4105" />
                  <p data-id="element-4106">You don't have any upcoming events</p>
                  <Link to="/events" className="text-sm underline mt-2 inline-block" data-id="element-4107">
                    Browse Events
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </>}
      {/* Saved Items Section */}
      {activeSection === 'saved' && <div data-id="element-4108">
          <h3 className="text-lg font-medium mb-2" data-id="element-4109">Saved Items</h3>
          {savedItems.length > 0 ? <div className="space-y-3" data-id="element-4110">
              {savedItems.map(item => <Link key={item.id} to={item.url} className="block border border-gray-300 rounded-lg p-4 hover:bg-gray-50" data-id="element-4111">
                  <div className="flex items-center" data-id="element-4112">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center border border-dashed border-gray-400 mr-3" data-id="element-4113">
                      <span className="text-gray-500" data-id="element-4114">{item.icon}</span>
                    </div>
                    <div className="flex-1" data-id="element-4115">
                      <div className="flex items-center" data-id="element-4116">
                        <h4 className="font-medium" data-id="element-4117">{item.name}</h4>
                        <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 border border-gray-200 rounded-full" data-id="element-4118">
                          {item.type === 'community' ? 'Community' : 'Event'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600" data-id="element-4119">
                        {item.description}
                      </p>
                    </div>
                    <button className="p-1 hover:bg-gray-100 rounded" data-id="element-4120">
                      <BookmarkIcon className="w-5 h-5 fill-gray-700 text-gray-700" data-id="element-4121" />
                    </button>
                  </div>
                </Link>)}
            </div> : <div className="border border-gray-300 rounded-lg p-8 text-center text-gray-500" data-id="element-4122">
              <BookmarkIcon className="w-10 h-10 mx-auto mb-2" data-id="element-4123" />
              <p data-id="element-4124">You haven't saved any items yet</p>
              <p className="text-sm mt-1" data-id="element-4125">Items you save will appear here</p>
            </div>}
        </div>}
      {/* Tickets Section */}
      {activeSection === 'tickets' && <div data-id="element-4126">
          <h3 className="text-lg font-medium mb-2" data-id="element-4127">My Tickets</h3>
          {myTickets.length > 0 ? <div className="space-y-4" data-id="element-4128">
              {myTickets.map(ticket => <div key={ticket.id} className="border border-gray-300 rounded-lg overflow-hidden" data-id="element-4129">
                  <div className="p-4" data-id="element-4130">
                    <div className="flex justify-between items-start" data-id="element-4131">
                      <h4 className="font-bold" data-id="element-4132">{ticket.eventName}</h4>
                      <span className="text-xs bg-gray-200 rounded-full px-2 py-0.5" data-id="element-4133">
                        {ticket.ticketType}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mt-2" data-id="element-4134">
                      <CalendarIcon className="w-4 h-4 mr-1" data-id="element-4135" />
                      <span data-id="element-4136">{ticket.date}</span>
                      <span className="mx-2" data-id="element-4137">•</span>
                      <span data-id="element-4138">{ticket.time}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mt-1" data-id="element-4139">
                      <MapPinIcon className="w-4 h-4 mr-1" data-id="element-4140" />
                      <span data-id="element-4141">{ticket.location}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-4142">
                      <div className="flex justify-between items-center" data-id="element-4143">
                        <div data-id="element-4144">
                          <span className="text-xs text-gray-500" data-id="element-4145">
                            Ticket ID:
                          </span>
                          <span className="text-sm font-medium ml-1" data-id="element-4146">
                            {ticket.ticketId}
                          </span>
                        </div>
                        <a href={ticket.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-gray-800 text-white rounded-md text-sm flex items-center" data-id="element-4147">
                          View Ticket
                          <ExternalLinkIcon className="w-3 h-3 ml-1" data-id="element-4148" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 border-t border-gray-200" data-id="element-4149">
                    <div className="flex items-center" data-id="element-4150">
                      <CheckCircleIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-4151" />
                      <span className="text-sm" data-id="element-4152">Ticket confirmed</span>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="border border-gray-300 rounded-lg p-8 text-center text-gray-500" data-id="element-4153">
              <TicketIcon className="w-10 h-10 mx-auto mb-2" data-id="element-4154" />
              <p data-id="element-4155">You don't have any tickets yet</p>
              <Link to="/events" className="text-sm underline mt-2 inline-block" data-id="element-4156">
                Browse Events
              </Link>
            </div>}
        </div>}
      {/* Settings Section */}
      {activeSection === 'settings' && <div data-id="element-4157">
          <div className="mb-6" data-id="element-4158">
            <div className="flex border-b border-gray-200 mb-6 overflow-x-auto" data-id="element-4159">
              <button onClick={() => setActiveSettingsTab('account')} className={`py-2 px-4 whitespace-nowrap ${activeSettingsTab === 'account' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4160">
                <UserIcon className="w-4 h-4 inline mr-1" data-id="element-4161" />
                Account Settings
              </button>
              <button onClick={() => setActiveSettingsTab('notifications')} className={`py-2 px-4 whitespace-nowrap ${activeSettingsTab === 'notifications' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4162">
                <BellIcon className="w-4 h-4 inline mr-1" data-id="element-4163" />
                Notifications
              </button>
              <button onClick={() => setActiveSettingsTab('privacy')} className={`py-2 px-4 whitespace-nowrap ${activeSettingsTab === 'privacy' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4164">
                <EyeIcon className="w-4 h-4 inline mr-1" data-id="element-4165" />
                Privacy
              </button>
              <button onClick={() => setActiveSettingsTab('connected')} className={`py-2 px-4 whitespace-nowrap ${activeSettingsTab === 'connected' ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4166">
                <GlobeIcon className="w-4 h-4 inline mr-1" data-id="element-4167" />
                Connected Accounts
              </button>
            </div>
            {activeSettingsTab === 'account' && <div data-id="element-4168">
                <h3 className="text-lg font-medium mb-4" data-id="element-4169">Account Settings</h3>
                <div className="space-y-6" data-id="element-4170">
                  {accountSettings.map(section => <div key={section.id} className="border border-gray-300 rounded-lg p-4" data-id="element-4171">
                      <div className="flex items-center mb-3" data-id="element-4172">
                        <div className="p-2 bg-gray-100 rounded-full mr-3" data-id="element-4173">
                          {section.icon}
                        </div>
                        <div data-id="element-4174">
                          <h4 className="font-medium" data-id="element-4175">{section.title}</h4>
                          <p className="text-xs text-gray-600" data-id="element-4176">
                            {section.description}
                          </p>
                        </div>
                      </div>
                      {section.fields && <div className="space-y-3" data-id="element-4177">
                          {section.fields.map((field, index) => <div key={index} className="flex flex-col" data-id="element-4178">
                              <label className="text-sm mb-1" data-id="element-4179">
                                {field.name}
                              </label>
                              {field.type === 'textarea' ? <textarea defaultValue={field.value} className="w-full p-2 border border-gray-300 rounded-md text-sm" rows={3} data-id="element-4180" /> : <input type={field.type} defaultValue={field.value} className="w-full p-2 border border-gray-300 rounded-md text-sm" data-id="element-4181" />}
                            </div>)}
                          <button className="bg-gray-800 text-white rounded-md px-4 py-2 text-sm mt-2" data-id="element-4182">
                            Save Changes
                          </button>
                        </div>}
                      {section.options && <div className="space-y-3" data-id="element-4183">
                          {section.options.map((option, index) => <div key={index} className="flex justify-between items-center" data-id="element-4184">
                              <span className="text-sm" data-id="element-4185">{option.name}</span>
                              {typeof option.value === 'boolean' ? <div className={`w-10 h-6 rounded-full p-1 ${option.value ? 'bg-gray-800' : 'bg-gray-300'}`} data-id="element-4186">
                                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${option.value ? 'translate-x-4' : ''}`} data-id="element-4187"></div>
                                </div> : <select className="p-1 border border-gray-300 rounded-md text-sm" data-id="element-4188">
                                  {option.choices?.map((choice, i) => <option key={i} selected={choice === option.value} data-id="element-4189">
                                      {choice}
                                    </option>)}
                                </select>}
                            </div>)}
                          <button className="bg-gray-800 text-white rounded-md px-4 py-2 text-sm mt-2" data-id="element-4190">
                            Save Changes
                          </button>
                        </div>}
                      {section.paymentMethods && <div data-id="element-4191">
                          <div className="space-y-3 mb-4" data-id="element-4192">
                            {section.paymentMethods.map((method, index) => <div key={index} className="flex justify-between items-center p-3 border border-gray-200 rounded-md" data-id="element-4193">
                                <div data-id="element-4194">
                                  <div className="font-medium" data-id="element-4195">
                                    {method.type}
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-4196">
                                    {method.number}
                                  </div>
                                </div>
                                <div className="flex items-center" data-id="element-4197">
                                  {method.default && <span className="text-xs bg-gray-200 rounded-full px-2 py-0.5 mr-2" data-id="element-4198">
                                      Default
                                    </span>}
                                  <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4199">
                                    Edit
                                  </button>
                                </div>
                              </div>)}
                          </div>
                          <button className="border border-gray-300 rounded-md px-4 py-2 text-sm w-full" data-id="element-4200">
                            + Add Payment Method
                          </button>
                        </div>}
                    </div>)}
                </div>
              </div>}
            {activeSettingsTab === 'notifications' && <div data-id="element-4201">
                <h3 className="text-lg font-medium mb-4" data-id="element-4202">
                  Notification Settings
                </h3>
                <div className="flex border-b border-gray-200 mb-4 overflow-x-auto" data-id="element-4203">
                  {notificationSettings.map(section => <button key={section.id} onClick={() => setActiveNotificationsTab(section.id)} className={`py-2 px-4 whitespace-nowrap text-sm ${activeNotificationsTab === section.id ? 'border-b-2 border-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-4204">
                      <div className="flex items-center" data-id="element-4205">
                        {section.icon}
                        <span className="ml-1" data-id="element-4206">{section.title}</span>
                      </div>
                    </button>)}
                </div>
                {notificationSettings.map(section => {
            if (section.id !== activeNotificationsTab) return null;
            return <div key={section.id} className="border border-gray-300 rounded-lg p-4" data-id="element-4207">
                      <h4 className="font-medium mb-4" data-id="element-4208">
                        {section.title} Preferences
                      </h4>
                      {section.options && <div className="space-y-3" data-id="element-4209">
                          {section.options.map((option, index) => <div key={index} className="flex justify-between items-center p-2 border-b border-gray-100" data-id="element-4210">
                              <span className="text-sm" data-id="element-4211">{option.name}</span>
                              <div className={`w-10 h-6 rounded-full p-1 ${option.value ? 'bg-gray-800' : 'bg-gray-300'}`} data-id="element-4212">
                                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${option.value ? 'translate-x-4' : ''}`} data-id="element-4213"></div>
                              </div>
                            </div>)}
                          <button className="bg-gray-800 text-white rounded-md px-4 py-2 text-sm mt-2" data-id="element-4214">
                            Save Preferences
                          </button>
                        </div>}
                      {section.devices && <div className="space-y-3" data-id="element-4215">
                          {section.devices.map((device, index) => <div key={index} className="flex justify-between items-center p-3 border border-gray-200 rounded-md" data-id="element-4216">
                              <div data-id="element-4217">
                                <div className="font-medium" data-id="element-4218">{device.name}</div>
                                <div className="text-xs text-gray-500" data-id="element-4219">
                                  Last active: {device.lastActive}
                                </div>
                              </div>
                              <div className="flex items-center" data-id="element-4220">
                                <span className={`text-xs rounded-full px-2 py-0.5 mr-2 ${device.status === 'active' ? 'bg-gray-200' : 'bg-gray-100'}`} data-id="element-4221">
                                  {device.status === 'active' ? 'Active' : 'Inactive'}
                                </span>
                                <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4222">
                                  Remove
                                </button>
                              </div>
                            </div>)}
                        </div>}
                      {section.mutedItems && <div className="space-y-3" data-id="element-4223">
                          {section.mutedItems.map((item, index) => <div key={index} className="flex justify-between items-center p-3 border border-gray-200 rounded-md" data-id="element-4224">
                              <div data-id="element-4225">
                                <div className="font-medium" data-id="element-4226">{item.name}</div>
                                <div className="text-xs text-gray-500" data-id="element-4227">
                                  {item.type}
                                </div>
                              </div>
                              <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4228">
                                Unmute
                              </button>
                            </div>)}
                        </div>}
                    </div>;
          })}
              </div>}
            {activeSettingsTab === 'privacy' && <div className="border border-gray-300 rounded-lg p-4" data-id="element-4229">
                <h3 className="font-medium mb-4" data-id="element-4230">Privacy Settings</h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-4231">
                  Control who can see your profile and how your data is used
                </p>
                <div className="space-y-4" data-id="element-4232">
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4233">
                    <div className="flex justify-between items-center" data-id="element-4234">
                      <div data-id="element-4235">
                        <h4 className="font-medium" data-id="element-4236">Profile Visibility</h4>
                        <p className="text-xs text-gray-500" data-id="element-4237">
                          Who can see your profile information
                        </p>
                      </div>
                      <select className="p-1 border border-gray-300 rounded-md text-sm" data-id="element-4238">
                        <option data-id="element-4239">Public</option>
                        <option data-id="element-4240">Friends Only</option>
                        <option data-id="element-4241">Private</option>
                      </select>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4242">
                    <div className="flex justify-between items-center" data-id="element-4243">
                      <div data-id="element-4244">
                        <h4 className="font-medium" data-id="element-4245">Activity Status</h4>
                        <p className="text-xs text-gray-500" data-id="element-4246">
                          Show when you're active on the platform
                        </p>
                      </div>
                      <div className="w-10 h-6 rounded-full p-1 bg-gray-800" data-id="element-4247">
                        <div className="w-4 h-4 rounded-full bg-white transform translate-x-4" data-id="element-4248"></div>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4249">
                    <div className="flex justify-between items-center" data-id="element-4250">
                      <div data-id="element-4251">
                        <h4 className="font-medium" data-id="element-4252">Data Usage</h4>
                        <p className="text-xs text-gray-500" data-id="element-4253">
                          Allow us to use your data for personalization
                        </p>
                      </div>
                      <div className="w-10 h-6 rounded-full p-1 bg-gray-300" data-id="element-4254">
                        <div className="w-4 h-4 rounded-full bg-white" data-id="element-4255"></div>
                      </div>
                    </div>
                  </div>
                  <button className="bg-gray-800 text-white rounded-md px-4 py-2 text-sm" data-id="element-4256">
                    Save Privacy Settings
                  </button>
                </div>
              </div>}
            {activeSettingsTab === 'connected' && <div className="border border-gray-300 rounded-lg p-4" data-id="element-4257">
                <h3 className="font-medium mb-4" data-id="element-4258">Connected Accounts</h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-4259">
                  Manage your connected social media and other accounts
                </p>
                <div className="space-y-3 mb-4" data-id="element-4260">
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4261">
                    <div className="flex justify-between items-center" data-id="element-4262">
                      <div className="flex items-center" data-id="element-4263">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-4264">
                          <span className="text-xs" data-id="element-4265">X</span>
                        </div>
                        <div data-id="element-4266">
                          <h4 className="font-medium" data-id="element-4267">Twitter (X)</h4>
                          <p className="text-xs text-gray-500" data-id="element-4268">
                            Connected as @johnkamau
                          </p>
                        </div>
                      </div>
                      <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4269">
                        Disconnect
                      </button>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4270">
                    <div className="flex justify-between items-center" data-id="element-4271">
                      <div className="flex items-center" data-id="element-4272">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-4273">
                          <span className="text-xs" data-id="element-4274">FB</span>
                        </div>
                        <div data-id="element-4275">
                          <h4 className="font-medium" data-id="element-4276">Facebook</h4>
                          <p className="text-xs text-gray-500" data-id="element-4277">Not connected</p>
                        </div>
                      </div>
                      <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4278">
                        Connect
                      </button>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4279">
                    <div className="flex justify-between items-center" data-id="element-4280">
                      <div className="flex items-center" data-id="element-4281">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-4282">
                          <span className="text-xs" data-id="element-4283">IG</span>
                        </div>
                        <div data-id="element-4284">
                          <h4 className="font-medium" data-id="element-4285">Instagram</h4>
                          <p className="text-xs text-gray-500" data-id="element-4286">
                            Connected as @johnkamau
                          </p>
                        </div>
                      </div>
                      <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4287">
                        Disconnect
                      </button>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md" data-id="element-4288">
                    <div className="flex justify-between items-center" data-id="element-4289">
                      <div className="flex items-center" data-id="element-4290">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-4291">
                          <span className="text-xs" data-id="element-4292">LI</span>
                        </div>
                        <div data-id="element-4293">
                          <h4 className="font-medium" data-id="element-4294">LinkedIn</h4>
                          <p className="text-xs text-gray-500" data-id="element-4295">
                            Connected as John Kamau
                          </p>
                        </div>
                      </div>
                      <button className="text-xs border border-gray-300 rounded-md px-2 py-1" data-id="element-4296">
                        Disconnect
                      </button>
                    </div>
                  </div>
                </div>
                <button className="border border-gray-300 rounded-md px-4 py-2 text-sm w-full" data-id="element-4297">
                  Connect Another Account
                </button>
              </div>}
          </div>
          <div className="space-y-3 mt-6" data-id="element-4298">
            <button className="w-full flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 justify-center" data-id="element-4299">
              <LogOutIcon className="w-5 h-5 text-gray-600 mr-3" data-id="element-4300" />
              <span data-id="element-4301">Logout</span>
            </button>
          </div>
        </div>}
    </div>;
};
export default UserProfile;